import { useState } from 'react';

/**
 * El botón de BORRAR un registro de campo, con su ventana de confirmación.
 *
 * Pedido del usuario del 9-set: *«me estaría faltando un botón en cada registro para eliminar el
 * registro subido»*, en las tres solapas de Registros de campo. Y sobre el cómo, cuando se le
 * planteó borrado real contra anulación: *«preferiría borrado real porque no debería haber rastro,
 * es error total, pero igual que quede documentado que se borró a la interna»*.
 *
 * Es UN componente para las tres solapas porque el borrado es la única acción del sistema que no
 * se puede deshacer desde la pantalla, y tres confirmaciones que se ven parecidas pero preguntan
 * distinto es exactamente como alguien borra lo que no quería. Lo que cambia por solapa es el
 * `sujeto` —qué registro es— y la función que borra; todo lo demás es idéntico.
 *
 * Tres decisiones de la ventana:
 *
 *  1. **Dice qué se va a perder, con nombre y fecha.** «¿Seguro?» a secas obliga a recordar sobre
 *     qué fila se hizo clic, y en una tabla de sesenta filas eso es adivinar.
 *  2. **El motivo es obligatorio**, y el texto explica por qué: es lo único que queda. El borrado
 *     es real, así que si nadie escribe el motivo, en un mes nadie va a poder decir qué se borró
 *     ni por qué. La constancia interna guarda la fila entera, pero la fila no dice el motivo.
 *  3. **El botón que confirma dice «Borrar definitivamente»**, no «Aceptar». La palabra tiene que
 *     nombrar la consecuencia; «Aceptar» se aprieta sin leer.
 */
export default function BorrarRegistro({ sujeto, queSePierde, onBorrar, onBorrado }: {
  /** Qué registro es, con lo que lo identifica: «Makro Surco · Lady Clarissa · 05/09». */
  sujeto: string;
  /** Lo que se va con él, si hay algo más que la fila. Ej.: «sus 8 líneas de producto». */
  queSePierde?: string;
  /** Llama a la RPC. Recibe el motivo ya validado y sin espacios de borde. */
  onBorrar: (motivo: string) => Promise<void>;
  /** Para que la bandeja recargue. */
  onBorrado: () => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState('');

  const cerrar = () => { setAbierto(false); setMotivo(''); setError(''); };

  const borrar = async () => {
    if (motivo.trim() === '') {
      setError('Escribe el motivo: el borrado es definitivo y esto es lo único que queda.');
      return;
    }
    setBorrando(true);
    setError('');
    try {
      await onBorrar(motivo.trim());
      cerrar();
      onBorrado();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo borrar.');
    } finally {
      setBorrando(false);
    }
  };

  if (!abierto) {
    return (
      <button
        type="button" onClick={() => setAbierto(true)}
        className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
      >
        Borrar
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <header className="border-b border-gray-200 px-5 py-4">
          <h2 className="text-base font-bold text-gray-900">Borrar este registro</h2>
          {/* El sujeto, para no depender de recordar en qué fila se hizo clic. */}
          <p className="mt-0.5 text-sm text-gray-700">{sujeto}</p>
        </header>

        <div className="space-y-3 px-5 py-4">
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <p className="font-semibold">Esto no se puede deshacer desde el sistema.</p>
            <p className="mt-1">
              El registro se borra de verdad{queSePierde ? `, junto con ${queSePierde}` : ''}. Queda
              una constancia interna con la fila completa y con tu nombre, pero la fila desaparece
              de las pantallas y de los totales.
            </p>
          </div>

          <label className="block text-sm">
            <span className="block font-medium text-gray-900">Motivo</span>
            <span className="block text-xs text-gray-500">
              Es lo único que va a explicar qué se borró. En un mes, sin esto, nadie va a poder
              reconstruir por qué.
            </span>
            <input
              type="text" value={motivo} autoFocus
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Lo cargó dos veces por error"
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1"
            />
          </label>

          {error !== '' && (
            <p role="alert" className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button
            type="button" onClick={cerrar}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button" onClick={() => void borrar()} disabled={borrando}
            className="rounded bg-red-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50"
          >
            {borrando ? 'Borrando…' : 'Borrar definitivamente'}
          </button>
        </footer>
      </div>
    </div>
  );
}
